"use client";

import { startTransition, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
  type Table,
} from "@tanstack/react-table";
import { createNumberRangeFilterFn } from "../utils/createNumberRangeFilterFn";
import { createCheckboxFilterFn } from "../utils/createCheckboxFilterFn";
import { createDateRangeFilterFn } from "../utils/createDateRangeFilterFn";

interface UseTableInstanceOptions<TData> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  enableSorting?: boolean;
  manualSorting?: boolean;
  initialSorting?: SortingState;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualFiltering?: boolean;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
}

export function useTableInstance<TData>({
  data,
  columns,
  enableSorting = false,
  manualSorting = false,
  initialSorting,
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
  manualFiltering = false,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange: controlledOnColumnFiltersChange,
}: UseTableInstanceOptions<TData>): Table<TData> {
  const [internalColumnFilters, setInternalColumnFilters] =
    useState<ColumnFiltersState>([]);
  const [internalSorting, setInternalSorting] = useState<SortingState>(
    initialSorting ?? [],
  );

  const sorting = controlledSorting ?? internalSorting;
  const onSortingChange = controlledOnSortingChange ?? setInternalSorting;
  const columnFilters = controlledColumnFilters ?? internalColumnFilters;
  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (...params) => {
    startTransition(() =>
      (controlledOnColumnFiltersChange ?? setInternalColumnFilters)(...params),
    );
  };

  const resolvedColumns = useMemo(
    () =>
      columns.map((col) => {
        if (col.filterFn) return col;
        if (col.meta?.filter?.type === "numberRange") {
          return { ...col, filterFn: createNumberRangeFilterFn<TData>() };
        }
        if (col.meta?.filter?.type === "checkbox") {
          return { ...col, filterFn: createCheckboxFilterFn<TData>() };
        }
        if (col.meta?.filter?.type === "dateRange") {
          return { ...col, filterFn: createDateRangeFilterFn<TData>() };
        }
        return col;
      }),
    [columns],
  );

  // eslint-disable-next-line react-hooks/incompatible-library
  return useReactTable({
    data,
    columns: resolvedColumns,
    state: { columnFilters, sorting },
    onColumnFiltersChange,
    onSortingChange,
    enableSorting,
    manualSorting,
    manualFiltering,
    // Without this, TanStack defaults numeric columns to desc-first — force
    // every column through the same unsorted -> asc -> desc -> unsorted cycle.
    sortDescFirst: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
}
